/* eslint-disable no-unused-expressions */
import { useSelector, useDispatch } from 'react-redux';
import debounce from 'lodash/debounce';
import React, {
  CSSProperties,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import Cookie from 'cookie';
import * as Bowser from 'bowser';

import { useCustomRouter } from 'src/shared/lib/history/hooks';
import { returnValueIfExists } from 'lib/server.service';
import {
  MOUSEDOWN_EVENT,
  ONE_DAY_IN_MS,
  RESIZE,
  VIEWER_DEVICE,
} from 'constants/constants';
import { Store } from 'app-redux/types/storeTypes';
import {
  setClientWidth,
  setMobileViewWidthState,
  setViewerDevice,
} from 'app-redux/actions/appActions';
import {
  GAReplacementValue,
  MetaReplacementValue,
  ViewerDevice,
} from 'constants/enums';
import { getStyles, setItemInObjectBySlug } from 'lib/sharedMethods.service';
import { parseJsonProperties } from 'src/lib/json.service';
import {
  Block,
  ImageWrapper,
  Link,
} from 'src/__generated__/graphqlTypes';
import { contentfulSlugs } from 'constants/objects';
import { replaceContentfulVariables } from 'lib/text.service';
import { resetClientWidthCookie } from 'src/shared/lib/cookie';

export const useClickOutside = (
  elementRef: React.MutableRefObject<any>,
  handler: (...rest: any) => void,
  ...handlerData: Array<any>
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!elementRef?.current?.contains(event.target)) {
        handler(...handlerData);
      }
    };

    document.addEventListener(MOUSEDOWN_EVENT, handleClickOutside);

    return () => document.removeEventListener(MOUSEDOWN_EVENT, handleClickOutside);
  }, [elementRef, handler]);
};

/** {@link useClickOutside} should use original method, this was done for fast fix */
export const useClickOutsideMultiple = (
  elementRef: React.MutableRefObject<any>[],
  handler: (...rest: any) => void,
  ...handlerData: Array<any>
) => {
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isClickOutside = elementRef.every(
        (ref) => ref.current && !ref.current.contains(event.target),
      );

      if (isClickOutside) {
        handler(...handlerData);
      }
    };

    document.addEventListener(MOUSEDOWN_EVENT, handleClickOutside);

    return () => document.removeEventListener(MOUSEDOWN_EVENT, handleClickOutside);
  }, [elementRef, handler]);
};

export const useWatchMobileViewWidth = (
  mobileViewMaxWidth: number,
  isMobileViewWidth: boolean,
): void => {
  const dispatch = useDispatch();
  const viewerDevice = useSelector((store: Store) => store.server.app.viewerDevice);
  const clientWidth = useSelector((store: Store) => store.client.app.clientWidth);

  const updateViewerDeviceIfRequired = useCallback(() => {
    const userAgent = Bowser.parse(window.navigator.userAgent);
    const isDesktopLikeTablet = (userAgent.os.name?.toLowerCase() === 'macos'
      || userAgent.os.name?.toLowerCase() === 'linux')
        && userAgent.platform.type?.toLowerCase() === 'desktop'
        && navigator.maxTouchPoints > 1;
    const platformType = isDesktopLikeTablet ? ViewerDevice.TABLET : userAgent.platform.type;

    if (platformType && platformType !== viewerDevice) {
      const cookieOptions: Cookie.CookieSerializeOptions = {
        secure: Boolean(process.env.NEXT_PUBLIC_CP_DOMAIN
          && window.location.origin.includes(process.env.NEXT_PUBLIC_CP_DOMAIN)),
        maxAge: ONE_DAY_IN_MS * 365,
      };
      document.cookie = Cookie.serialize(VIEWER_DEVICE, platformType, cookieOptions);
      dispatch(setMobileViewWidthState(platformType === ViewerDevice.MOBILE));
      dispatch(setViewerDevice(platformType as ViewerDevice));
    }
  }, [viewerDevice]);

  const setMobileWidthIfRequired = useCallback(() => {
    const isNowMobileViewWidth = mobileViewMaxWidth >= document.body.clientWidth;

    if (isMobileViewWidth !== isNowMobileViewWidth && viewerDevice === ViewerDevice.DESKTOP) {
      dispatch(setMobileViewWidthState(isNowMobileViewWidth));
    }
  },
  [
    mobileViewMaxWidth,
    isMobileViewWidth,
    viewerDevice,
    dispatch,
  ]);

  const setClientWidthIfRequired = useCallback(debounce(() => {
    if (clientWidth !== document.body.clientWidth) {
      dispatch(setClientWidth(document.body.clientWidth));
      resetClientWidthCookie();
    }
  }, 300), [dispatch, clientWidth]);

  useEffect(() => {
    setMobileWidthIfRequired();
    setClientWidthIfRequired();

    const handler = debounce(() => {
      setMobileWidthIfRequired();
      setClientWidthIfRequired();
      updateViewerDeviceIfRequired();
    }, 50);
    window.addEventListener(RESIZE, handler);

    return () => window.removeEventListener(RESIZE, handler);
  }, [setMobileWidthIfRequired, setClientWidthIfRequired]);
};

export const useCommonDataButtonsProps = (
  gaData?: Partial<Record<GAReplacementValue, string>>,
  link?: Link,
) => {
  const [pathName, setPathName] = useState<string>('');
  const [queryString, setQueryString] = useState<string>('');
  const router = useCustomRouter();

  useEffect(() => {
    setPathName(router.asPath.split('?')[0]);
    setQueryString(router.asPath.includes('?') ? `?${router.asPath.split('?')[1]}` : '');
  }, [router.asPath]);

  const baseUrl = process.env.BASE_SERVER_URL || process.env.NEXT_PUBLIC_BASE_SERVER_URL;
  const styles = getStyles(link);
  const replacementEntry = gaData
    ? {
      [GAReplacementValue.SHORT_URL]: gaData[GAReplacementValue.SHORT_URL] || pathName,
      [GAReplacementValue.FULL_URL]: gaData[GAReplacementValue.FULL_URL] || `${baseUrl}${pathName}`,
      [GAReplacementValue.FULL_URL_WITH_QUERY]: gaData[GAReplacementValue.FULL_URL_WITH_QUERY] || `${baseUrl}${pathName}${queryString}`,
      ...gaData,
    }
    : {
      [GAReplacementValue.SHORT_URL]: pathName,
      [GAReplacementValue.FULL_URL]: `${baseUrl}${pathName}`,
      [GAReplacementValue.FULL_URL_WITH_QUERY]: `${baseUrl}${pathName}${queryString}`,
    };
  const parsedGaProperties = parseJsonProperties(link?.gaProperties, replacementEntry);

  return { styles, parsedGaProperties };
};

export const useCommonContentfulBlocks = <T extends string, K>(
  blocks: Array<Block> = [],
  slugs: typeof contentfulSlugs = contentfulSlugs,
): Record<T, K> & { commonPageMaxWidth?: string; } => {
  const reducer = setItemInObjectBySlug(slugs);
  const { commonPageMaxWidth } = useSelector((store: Store) => store.server.app.scssVariables);
  const content = blocks.reduce(
    reducer,
    {} as Record<T, K>,
  );

  return { ...content, commonPageMaxWidth };
};

export const useTextWithReplacedValues = (
  replacementValues?: Partial<Record<MetaReplacementValue, string>>,
  text?: string | Array<string>,
) => {
  if (!replacementValues || !Object.keys(replacementValues).length || !text) {
    return text;
  }

  if (Array.isArray(text)) {
    return text.map((text) => {
      if (!text || typeof text !== 'string') {
        return text;
      }

      return replaceContentfulVariables(text.replace(/\\/g, ''), replacementValues);
    });
  }

  if (typeof text !== 'string') {
    return text;
  }

  return replaceContentfulVariables(text.replace('\\', ''), replacementValues);
};

export const useRefState = <T>(initial: T): [T, (value: T) => void] => {
  const ref = useRef<T>(initial);

  const setValue = (value: T) => {
    ref.current = value;
  };

  return [ref.current, setValue];
};

export const useButtonBgImageStyles = (link?: Block['link'], backgroundSize?: CSSProperties['backgroundSize']) => {
  const viewerDevice = useSelector((store: Store) => store.server.app.viewerDevice);

  if (!link || !link.backgroundsCollection?.items?.length) {
    return null;
  }

  const backgrounds = link.backgroundsCollection.items
    .reduce((store, image) => ({
      ...store,
      [image?.slug || '']: image!,
    }), {} as Record<string, ImageWrapper | undefined>);

  const currentBackground = backgrounds[viewerDevice] || backgrounds.desktop || null;

  if (!currentBackground?.image?.url) {
    return null;
  }

  const { image } = currentBackground;
  const style: CSSProperties = {
    background: `url(${currentBackground!.image!.url})`,
    height: `${currentBackground.height || image.height}px`,
    width: `${currentBackground.width || image.width}px`,
    ...returnValueIfExists('backgroundSize', backgroundSize),
  };

  return style;
};
